// API Service for backend communication

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

class ApiService {
  /**
   * Create or ensure user exists in backend
   * @param {Object} user - Cognito user object with profile
   * @param {string} idToken - JWT ID token from Cognito
   * @returns {Promise<Object>} User data from backend
   */
  async createUser(user, idToken) {
    const { sub, given_name, family_name, email } = user.profile;

    // Construct display name from Cognito profile
    const displayName = given_name && family_name
      ? `${given_name} ${family_name}`
      : given_name || email?.split('@')[0] || 'User';

    const payload = {
      user_type: 'individual', // Default to individual, can be changed later
      display_name: displayName,
      email: email
    };

    try {
      console.log('Creating user with payload:', payload);
      console.log('API URL:', `${API_URL}/api/users`);

      const response = await fetch(`${API_URL}/api/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify(payload)
      });

      console.log('Response status:', response.status);

      const data = await response.json();
      console.log('Response data:', data);

      // 201 = Created, 409 = User already exists (both are OK)
      if (response.status === 201) {
        console.log('User created in backend:', data);
        return data;
      }

      if (response.status === 409) {
        console.log('User already exists in backend:', data);
        return data.user; // 409 returns { error: "...", user: {...} }
      }

      // Other errors
      const errorMsg = data.error || `Failed to create user (status: ${response.status})`;
      throw new Error(errorMsg);
    } catch (error) {
      console.error('Error creating user in backend:', error);
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Cannot connect to API server. Make sure it is running on port 3001.');
      }
      throw error;
    }
  }

  /**
   * Get current user profile from backend
   * @param {string} idToken - JWT ID token
   * @returns {Promise<Object>} User profile
   */
  async getCurrentUser(idToken) {
    try {
      const response = await fetch(`${API_URL}/api/users/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get user');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching user from backend:', error);
      throw error;
    }
  }

  /**
   * Update user profile
   * @param {string} idToken - JWT ID token
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated user
   */
  async updateUser(idToken, updates) {
    try {
      const response = await fetch(`${API_URL}/api/users/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify(updates)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update user');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  /**
   * Accept terms and conditions
   * @param {string} idToken - JWT ID token
   * @returns {Promise<Object>} Response with success status
   */
  async acceptTerms(idToken) {
    try {
      const response = await fetch(`${API_URL}/api/users/me/accept-terms`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to accept terms');
      }

      return await response.json();
    } catch (error) {
      console.error('Error accepting terms:', error);
      throw error;
    }
  }

  /**
   * Search users by name or email
   * @param {string} idToken - JWT ID token
   * @param {string} query - Search query
   * @param {number} limit - Maximum number of results (default 10)
   * @returns {Promise<Object>} Search results
   */
  async searchUsers(idToken, query, limit = 10) {
    try {
      const params = new URLSearchParams({
        q: query,
        limit: limit.toString()
      });

      const response = await fetch(`${API_URL}/api/users/search?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to search users');
      }

      return await response.json();
    } catch (error) {
      console.error('Error searching users:', error);
      throw error;
    }
  }

  /**
   * Get user statistics
   * @param {string} idToken - JWT ID token
   * @returns {Promise<Object>} User statistics
   */
  async getUserStats(idToken) {
    try {
      const response = await fetch(`${API_URL}/api/stats/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get user stats');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching user stats:', error);
      throw error;
    }
  }

  /**
   * Get user balances
   * @param {string} idToken - JWT ID token
   * @param {number} minBalance - Optional minimum balance filter
   * @returns {Promise<Object>} User balances
   */
  async getUserBalances(idToken, minBalance = null) {
    try {
      const url = minBalance !== null
        ? `${API_URL}/api/balances?min_balance=${minBalance}`
        : `${API_URL}/api/balances`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get balances');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching balances:', error);
      throw error;
    }
  }

  /**
   * Get transaction history
   * @param {string} idToken - JWT ID token
   * @param {Object} options - Query options (type, limit, offset)
   * @returns {Promise<Object>} Transaction history
   */
  async getTransactions(idToken, options = {}) {
    try {
      const params = new URLSearchParams();
      if (options.type) params.append('type', options.type);
      if (options.limit) params.append('limit', options.limit);
      if (options.offset) params.append('offset', options.offset);

      const url = `${API_URL}/api/transactions${params.toString() ? '?' + params.toString() : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get transactions');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching transactions:', error);
      throw error;
    }
  }

  /**
   * Get user's coins (coins created by the user)
   * @param {string} idToken - JWT ID token
   * @returns {Promise<Object>} User's created coins
   */
  async getMyCoins(idToken) {
    try {
      const response = await fetch(`${API_URL}/api/coins/my-coins`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get coins');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching coins:', error);
      throw error;
    }
  }

  /**
   * Send a coin to another user
   * @param {string} idToken - JWT ID token
   * @param {number} coinId - The coin ID to send
   * @param {string} receiverId - The receiver's user ID
   * @param {string} [senderId] - Optional sender ID (for organization admins sending from org)
   * @returns {Promise<Object>} Transaction result
   */
  async sendCoin(idToken, coinId, receiverId, senderId = null) {
    try {
      const body = {
        coin_id: coinId,
        receiver_id: receiverId
      };

      // Only include sender_id if explicitly provided
      if (senderId) {
        body.sender_id = senderId;
      }

      const response = await fetch(`${API_URL}/api/transactions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send coin');
      }

      return data;
    } catch (error) {
      console.error('Error sending coin:', error);
      throw error;
    }
  }

  /**
   * Request a coin from another user (creates pending transaction)
   * @param {string} idToken - JWT ID token
   * @param {number} coinId - The coin ID to request
   * @param {string} senderId - The user ID to request from (they will be the sender)
   * @returns {Promise<Object>} Transaction result
   */
  async requestCoin(idToken, coinId, senderId) {
    try {
      const response = await fetch(`${API_URL}/api/transactions/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          coin_id: coinId,
          sender_id: senderId
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to request coin');
      }

      return data;
    } catch (error) {
      console.error('Error requesting coin:', error);
      throw error;
    }
  }

  /**
   * Create a new coin
   * @param {string} idToken - JWT ID token
   * @param {string} coinType - 'individual' or 'organizational'
   * @param {string} description - Coin description
   * @returns {Promise<Object>} Created coin
   */
  async createCoin(idToken, coinType, description, issuerId = null) {
    try {
      const body = {
        coin_type: coinType,
        description: description
      };

      if (issuerId) {
        body.issuer_id = issuerId;
      }

      const response = await fetch(`${API_URL}/api/coins`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create coin');
      }

      return data;
    } catch (error) {
      console.error('Error creating coin:', error);
      throw error;
    }
  }

  /**
   * Update coin description
   * @param {string} idToken - JWT ID token
   * @param {number} coinId - The coin ID to update
   * @param {string} description - New description
   * @returns {Promise<Object>} Updated coin
   */
  async updateCoinDescription(idToken, coinId, description) {
    try {
      const response = await fetch(`${API_URL}/api/coins/${coinId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          description: description
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update coin');
      }

      return data;
    } catch (error) {
      console.error('Error updating coin:', error);
      throw error;
    }
  }

  /**
   * Approve a pending transaction
   * @param {string} idToken - JWT ID token
   * @param {string} txId - The transaction ID to approve
   * @param {string} [description] - Optional updated description for the coin
   * @returns {Promise<Object>} Updated transaction
   */
  async approveTransaction(idToken, txId, description) {
    try {
      const body = {};
      if (description !== undefined && description !== null) {
        body.description = description;
      }

      const response = await fetch(`${API_URL}/api/transactions/${txId}/approve`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to approve transaction');
      }

      return data;
    } catch (error) {
      console.error('Error approving transaction:', error);
      throw error;
    }
  }

  /**
   * Deny a pending transaction (deletes transaction and coin)
   * @param {string} idToken - JWT ID token
   * @param {string} txId - The transaction ID to deny
   * @returns {Promise<Object>} Result
   */
  async denyTransaction(idToken, txId) {
    try {
      const response = await fetch(`${API_URL}/api/transactions/${txId}/deny`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to deny transaction');
      }

      return data;
    } catch (error) {
      console.error('Error denying transaction:', error);
      throw error;
    }
  }

  /**
   * Get another user's public profile
   * @param {string} idToken - JWT ID token
   * @param {string} userId - User ID to fetch
   * @returns {Promise<Object>} User profile
   */
  async getUserProfile(idToken, userId) {
    try {
      const response = await fetch(`${API_URL}/api/users/${userId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get user profile');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching user profile:', error);
      throw error;
    }
  }

  /**
   * Get another user's public balances
   * @param {string} idToken - JWT ID token
   * @param {string} userId - User ID to fetch balances for
   * @param {number} minBalance - Optional minimum balance filter
   * @returns {Promise<Object>} User balances
   */
  async getUserBalancesById(idToken, userId, minBalance = null) {
    try {
      const url = minBalance !== null
        ? `${API_URL}/api/users/${userId}/balances?min_balance=${minBalance}`
        : `${API_URL}/api/users/${userId}/balances`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get user balances');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching user balances:', error);
      throw error;
    }
  }

  /**
   * Get another user's public transactions
   * @param {string} idToken - JWT ID token
   * @param {string} userId - User ID to fetch transactions for
   * @param {Object} options - Query options (type, limit, offset)
   * @returns {Promise<Object>} User transactions
   */
  async getUserTransactionsById(idToken, userId, options = {}) {
    try {
      const params = new URLSearchParams();
      if (options.type) params.append('type', options.type);
      if (options.limit) params.append('limit', options.limit);
      if (options.offset) params.append('offset', options.offset);

      const url = `${API_URL}/api/users/${userId}/transactions${params.toString() ? '?' + params.toString() : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get user transactions');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching user transactions:', error);
      throw error;
    }
  }

  async getTransactionById(idToken, txId) {
    try {
      const response = await fetch(`${API_URL}/api/transactions/${txId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get transaction details');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching transaction details:', error);
      throw error;
    }
  }

  async getPublicTransactionById(txId) {
    try {
      const response = await fetch(`${API_URL}/api/transactions/public/${txId}`, {
        method: 'GET'
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get transaction details');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching public transaction details:', error);
      throw error;
    }
  }

  async getUserOrganizations(idToken) {
    try {
      const response = await fetch(`${API_URL}/api/organizations/my-organizations`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get user organizations');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching user organizations:', error);
      throw error;
    }
  }

  async getOrganizationMembers(idToken, organizationId) {
    try {
      const response = await fetch(`${API_URL}/api/organizations/${organizationId}/members`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get organization members');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching organization members:', error);
      throw error;
    }
  }

  async getPendingCoinRequests(idToken, senderId) {
    try {
      const response = await fetch(`${API_URL}/api/users/${senderId}/transactions?type=sent&pending=true`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get pending coin requests');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching pending coin requests:', error);
      throw error;
    }
  }

  async approveCoinRequest(idToken, txId, description) {
    try {
      const body = {};
      if (description !== undefined && description !== null) {
        body.description = description;
      }

      const response = await fetch(`${API_URL}/api/transactions/${txId}/approve`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to approve coin request');
      }

      return data;
    } catch (error) {
      console.error('Error approving coin request:', error);
      throw error;
    }
  }

  async denyCoinRequest(idToken, txId) {
    try {
      const response = await fetch(`${API_URL}/api/transactions/${txId}/deny`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to deny coin request');
      }

      return await response.json();
    } catch (error) {
      console.error('Error denying coin request:', error);
      throw error;
    }
  }

  async inviteOrganizationMember(idToken, organizationId, userId, isAdmin = false) {
    try {
      const response = await fetch(`${API_URL}/api/organizations/${organizationId}/members`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user_id: userId, is_admin: isAdmin })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to send invitation');
      }

      return await response.json();
    } catch (error) {
      console.error('Error inviting organization member:', error);
      throw error;
    }
  }

  async getMyOrganizationInvites(idToken) {
    try {
      const response = await fetch(`${API_URL}/api/organizations/my-invites`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to get organization invites');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching organization invites:', error);
      throw error;
    }
  }

  async acceptOrganizationInvite(idToken, inviteId) {
    try {
      const response = await fetch(`${API_URL}/api/organizations/invites/${inviteId}/accept`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to accept invitation');
      }

      return await response.json();
    } catch (error) {
      console.error('Error accepting organization invite:', error);
      throw error;
    }
  }

  async declineOrganizationInvite(idToken, inviteId) {
    try {
      const response = await fetch(`${API_URL}/api/organizations/invites/${inviteId}/decline`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to decline invitation');
      }

      return await response.json();
    } catch (error) {
      console.error('Error declining organization invite:', error);
      throw error;
    }
  }

  async removeOrganizationMember(idToken, organizationId, userId) {
    try {
      const response = await fetch(`${API_URL}/api/organizations/${organizationId}/members/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to remove member');
      }

      return await response.json();
    } catch (error) {
      console.error('Error removing organization member:', error);
      throw error;
    }
  }

  async updateOrganizationMemberPrivilege(idToken, organizationId, userId, isAdmin) {
    try {
      const response = await fetch(`${API_URL}/api/organizations/${organizationId}/members/${userId}/privilege`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({ is_admin: isAdmin })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update member privilege');
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating organization member privilege:', error);
      throw error;
    }
  }

  // Bug Reports
  async createBugReport(idToken, bugReportData) {
    try {
      const response = await fetch(`${API_URL}/api/bug-reports`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          title: bugReportData.title,
          description: bugReportData.description,
          stepsToReproduce: bugReportData.stepsToReproduce,
          expectedBehavior: bugReportData.expectedBehavior,
          actualBehavior: bugReportData.actualBehavior,
          severity: bugReportData.severity,
          pageUrl: bugReportData.pageUrl,
          browserInfo: bugReportData.browserInfo
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit bug report');
      }

      return data;
    } catch (error) {
      console.error('Error creating bug report:', error);
      throw error;
    }
  }

  async getMyBugReports(idToken, limit = 50, offset = 0) {
    try {
      const response = await fetch(`${API_URL}/api/bug-reports/my?limit=${limit}&offset=${offset}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch bug reports');
      }

      return data;
    } catch (error) {
      console.error('Error fetching bug reports:', error);
      throw error;
    }
  }

  async getBugReports(idToken, params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.offset) queryParams.append('offset', params.offset);
      if (params.is_open !== undefined) queryParams.append('is_open', params.is_open);
      if (params.severity) queryParams.append('severity', params.severity);
      if (params.resolution_status) queryParams.append('resolution_status', params.resolution_status);

      const response = await fetch(`${API_URL}/api/bug-reports?${queryParams.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch bug reports');
      }

      return data;
    } catch (error) {
      console.error('Error fetching bug reports:', error);
      throw error;
    }
  }

  async updateBugReportResolution(idToken, reportId, resolutionStatus, developerNote) {
    try {
      const response = await fetch(`${API_URL}/api/bug-reports/${reportId}/resolution`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          resolution_status: resolutionStatus,
          developer_note: developerNote
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update bug report');
      }

      return data;
    } catch (error) {
      console.error('Error updating bug report:', error);
      throw error;
    }
  }

  async closeBugReport(idToken, reportId) {
    try {
      const response = await fetch(`${API_URL}/api/bug-reports/${reportId}/close`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to close bug report');
      }

      return data;
    } catch (error) {
      console.error('Error closing bug report:', error);
      throw error;
    }
  }

  async reopenBugReport(idToken, reportId) {
    try {
      const response = await fetch(`${API_URL}/api/bug-reports/${reportId}/reopen`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to reopen bug report');
      }

      return data;
    } catch (error) {
      console.error('Error reopening bug report:', error);
      throw error;
    }
  }

  async deleteBugReport(idToken, reportId) {
    try {
      const response = await fetch(`${API_URL}/api/bug-reports/${reportId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to delete bug report');
      }

      return data;
    } catch (error) {
      console.error('Error deleting bug report:', error);
      throw error;
    }
  }
}

export default new ApiService();
